import axios from "axios";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../layout/Footer";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import DriftSummaryCards, { usd } from "../../components/DriftSummaryCards";
import { formatDate } from "../../../constants";

/**
 * Billing Drift - companies whose Stripe subscription charges something other than what
 * their users say it should.
 *
 * The list is served from the local amountBreakdown mirror, so it costs no Stripe calls
 * and can be paged freely. "Recheck live" goes to Stripe for one company.
 *
 * Every number rendered here is a field on the response - the endpoint does the maths so
 * the two can never disagree.
 */

const DRIFT_TYPES = [
  { value: "UNDER", label: "Under-billed" },
  { value: "OVER", label: "Over-billed" },
  { value: "ALL", label: "Any drift" },
  { value: "NONE", label: "In sync" },
];

const COHORTS = [
  { value: "AUTO_PAY", label: "Auto-pay, not trialing" },
  { value: "TRIALING", label: "Trialing" },
  { value: "MANUAL_PAY", label: "Manual pay" },
  { value: "ALL", label: "All companies" },
];

const REASON_HELP = {
  TRIAL_MODE_GAP:
    "Seats or add-ons were granted while the company was in trial mode, so they were never added to Stripe.",
  MANUAL_PAY_GAP: "Company is on manual pay, so nothing is charged through Stripe.",
  OVER_BILLED: "Stripe charges for more than the company currently has. Reported only - never auto-corrected.",
  UNDER_BILLED: "Stripe charges for less than the company currently has.",
  STALE_BREAKDOWN:
    "The local copy of this subscription has not been refreshed recently. Recheck live before trusting this row.",
  NO_DRIFT: "In sync.",
};

const BillingDrift = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 0, totalCount: 0 });
  const [loader, setLoader] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [driftType, setDriftType] = useState("UNDER");
  const [cohort, setCohort] = useState("AUTO_PAY");
  const [search, setSearch] = useState("");
  const [minDeltaUsd, setMinDeltaUsd] = useState(0);

  const [expanded, setExpanded] = useState(null);
  const [liveResult, setLiveResult] = useState({});
  const [rechecking, setRechecking] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [syncModalCompanyId, setSyncModalCompanyId] = useState(null);
  const [syncProrationMode, setSyncProrationMode] = useState("next-cycle"); // safe default - no charge
  const [driftAlertCompanyId, setDriftAlertCompanyId] = useState(null);

  // Debounced copy of `search`. Every keystroke used to re-create fetchReport and re-fire the
  // effect, and each request makes the backend load the WHOLE cohort plus two aggregates -
  // typing "acme" meant four concurrent full-cohort scans. (BR-32)
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Monotonic request id. Without it a slower earlier response could resolve last and overwrite
  // the results for a newer query - the table showing rows for "ac" while the box reads "acme".
  const requestSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    const element = document.getElementById("page-container");
    element.classList.add("show");
  }, []);

  const fetchReport = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoader(true);
    try {
      const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/billing-drift`;
      const response = await axios.post(
        myurl,
        {
          auth_code: process.env.REACT_APP_AUTH_CODE,
          page: currentPage,
          limit: pageSize,
          driftType,
          cohort,
          search: debouncedSearch,
          minDeltaUsd,
          sortBy: driftType === "OVER" ? "overBilled" : "underBilled",
          sortOrder: "desc",
        },
        { headers: { "Content-Type": "application/json" } }
      );

      // Ignore a response that a newer request has already superseded.
      if (seq !== requestSeq.current) return;

      if (response.data.success === true) {
        const { rows: reportRows, summary: reportSummary, pagination: reportPagination } = response.data.data;
        setRows(reportRows || []);
        setSummary(reportSummary || null);
        setPagination(reportPagination || { currentPage: 1, totalPages: 0, totalCount: 0 });
      } else {
        toast.error(response.data.message || "Failed to load the billing drift report");
      }
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("Error fetching billing drift report:", error);
      toast.error(error.response?.data?.message || "Failed to load the billing drift report");
    } finally {
      if (seq === requestSeq.current) setLoader(false);
    }
  }, [currentPage, pageSize, driftType, cohort, debouncedSearch, minDeltaUsd]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const recheckLive = async (companyId) => {
    setRechecking(companyId);
    try {
      const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/billing-drift/recheck`;
      const response = await axios.post(
        myurl,
        { auth_code: process.env.REACT_APP_AUTH_CODE, companyId },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success === true) {
        setLiveResult((prev) => ({ ...prev, [companyId]: response.data.data }));
        setExpanded(companyId);
        toast.success("Rechecked against Stripe");
        if (response.data.data.developerNotified) {
          toast.info("Developer team has already been notified of this error.");
        }

        if (response.data.data.comparable === false) {
          setDriftAlertCompanyId(companyId);
        } else {
          // Auto-dismiss if this company's modal was open and the issue is now resolved
          // (e.g. after Reset subscription succeeds and the follow-up recheck comes back clean).
          setDriftAlertCompanyId((prev) => (prev === companyId ? null : prev));
        }
      } else {
        toast.error(response.data.message || "Recheck failed");
      }
    } catch (error) {
      console.error("Error rechecking billing drift:", error);
      toast.error(error.response?.data?.message || "Recheck failed");
      if (error.response?.data?.data?.developerNotified) {
        toast.info("Developer team has already been notified of this error.");
      }
    } finally {
      setRechecking(null);
    }
  };

  // Re-checks against live Stripe at write time (never trusts stale `rows`/`liveResult`) and,
  // if still out of sync, raises the affected item quantities/prices. `prorationMode` is the
  // SuperAdmin's explicit choice from the Sync popup - 'next-cycle' (no charge) or 'charge-now'
  // (prorated charge for the current period, immediately). Over-billed companies are never
  // touched or credited - under-billing/price-drift correction only. Invoked only from the Sync
  // popup's "Confirm Sync" button, never directly from the row.
  const syncCompany = async (companyId, prorationMode) => {
    setSyncing(companyId);
    try {
      const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/billing-drift/sync`;
      const response = await axios.post(
        myurl,
        { auth_code: process.env.REACT_APP_AUTH_CODE, companyId, prorationMode },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data?.data || {};
      if (response.data?.success && result.applied) {
        toast.success(
          result.prorationMode === "charge-now"
            ? `Synced - ${usd(result.underBilledUsd)}/mo corrected and charged now`
            : `Synced - ${usd(result.underBilledUsd)}/mo corrected, effective next cycle (no charge)`
        );
      } else if (response.data?.success && result.reason === "ALREADY_RECONCILED") {
        toast.success("Already in sync - no charge made");
      } else {
        toast.error(response.data?.message || "Sync failed");
        if (result.developerNotified) {
          toast.info("Developer team has already been notified of this error.");
        }
      }

      fetchReport();
      recheckLive(companyId);
    } catch (error) {
      console.error("Error syncing billing drift:", error);
      toast.error(error.response?.data?.message || "Sync failed");
      if (error.response?.data?.data?.developerNotified) {
        toast.info("Developer team has already been notified of this error.");
      }
    } finally {
      setSyncing(null);
      setSyncModalCompanyId(null);
    }
  };

  // Fixes a company whose stripeSubscriptionId no longer resolves in Stripe (e.g. after the
  // environment's Stripe account/mode was switched) by disabling Auto Pay and re-enabling it -
  // reusing the same two endpoints the Company edit modal already exposes for this, rather than
  // adding a dedicated "resync" API. `switch-to-manual` unconditionally detaches the dead
  // subscription reference; `restart-subscription` then creates a brand-new one, since its
  // "already active" guard reads the now-null currentSubscription and gets out of the way.
  const resetSubscription = async (companyId) => {
    if (
      !window.confirm(
        "Reset this company's subscription? This will disable Auto Pay (detaching the current " +
          "Stripe reference) and then re-enable it, creating a brand-new Stripe subscription and " +
          "emailing a fresh payment link to the company. Continue?"
      )
    ) {
      return;
    }

    setResetting(companyId);
    try {
      const authPayload = { auth_code: process.env.REACT_APP_AUTH_CODE, id: companyId };
      const base = process.env.REACT_APP_base_url;

      const disableResponse = await axios.post(`${base}api/masterAdmin/switch-to-manual`, authPayload, {
        headers: { "Content-Type": "application/json" },
      });

      // "Company is already on manual billing." just means the disable step is a no-op here -
      // fine, proceed straight to re-enabling.
      if (disableResponse.data.success !== true && disableResponse.data.message !== "Company is already on manual billing.") {
        toast.error(disableResponse.data.message || "Failed to disable Auto Pay");
        return;
      }

      const enableResponse = await axios.post(`${base}api/masterAdmin/restart-subscription`, authPayload, {
        headers: { "Content-Type": "application/json" },
      });

      if (enableResponse.data.success !== true) {
        toast.error(
          (enableResponse.data.message || "Failed to re-enable Auto Pay") +
            " - the company is currently on manual pay. Click Reset again to retry."
        );
        fetchReport();
        return;
      }

      toast.success("Subscription reset - a fresh payment link was emailed to the company.");
      fetchReport();
      recheckLive(companyId);
    } catch (error) {
      console.error("Error resetting subscription:", error);
      toast.error(error.response?.data?.message || "Failed to reset the subscription");
    } finally {
      setResetting(null);
    }
  };

  const exportCsv = async () => {
    // Export the WHOLE report, not the visible page. The button gave no hint of its scope, so a
    // 500-company report exported 20 rows and that file got handed to finance. (BR-33)
    let exportRows = rows;

    if (pagination.totalCount > rows.length) {
      try {
        const myurl = `${process.env.REACT_APP_base_url}api/masterAdmin/billing-drift`;
        const response = await axios.post(
          myurl,
          {
            auth_code: process.env.REACT_APP_AUTH_CODE,
            page: 1,
            limit: 100,
            driftType,
            cohort,
            search: debouncedSearch,
            minDeltaUsd,
            sortBy: driftType === "OVER" ? "overBilled" : "underBilled",
            sortOrder: "desc",
          },
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data.success === true) {
          const { rows: allRows, pagination: p } = response.data.data;
          exportRows = allRows || [];

          // The endpoint caps `limit` at 100, so page through the rest.
          for (let page = 2; page <= (p?.totalPages || 1); page += 1) {
            /* eslint-disable no-await-in-loop */
            const next = await axios.post(
              myurl,
              {
                auth_code: process.env.REACT_APP_AUTH_CODE,
                page,
                limit: 100,
                driftType,
                cohort,
                search: debouncedSearch,
                minDeltaUsd,
                sortBy: driftType === "OVER" ? "overBilled" : "underBilled",
                sortOrder: "desc",
              },
              { headers: { "Content-Type": "application/json" } }
            );
            /* eslint-enable no-await-in-loop */
            if (next.data.success === true) exportRows = exportRows.concat(next.data.data.rows || []);
          }
        }
      } catch (error) {
        console.error("Error building full CSV export:", error);
        toast.error("Could not load every page for export - exporting the current page only");
      }
    }

    const headers = [
      "Company", "Email", "Subscription", "Status", "Active Users",
      "Under-billed / mo", "Over-billed / mo", "Net / mo", "Remediable", "Stale", "Reasons",
    ];
    const escape = (value) => {
      const str = value === null || value === undefined ? "" : String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [headers.join(",")].concat(
      exportRows.map((row) =>
        [
          row.companyName, row.companyEmail, row.stripeSubscriptionId, row.subscriptionStatus,
          row.activeUserCount, row.monthlyUnderBilledUsd, row.monthlyOverBilledUsd, row.monthlyNetUsd,
          row.remediable ? "YES" : "NO", row.stale ? "YES" : "NO", (row.reasons || []).join("|"),
        ].map(escape).join(",")
      )
    );

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `billing-drift-${new Date().toISOString().slice(0, 10)}.csv`;

    // The anchor must be in the document, and the object URL must outlive the click. Revoking
    // synchronously right after click() races the download - in Firefox, with a detached
    // anchor, it could do nothing at all. (BR-33)
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
      link.remove();
    }, 0);

    toast.success(`Exported ${exportRows.length} row(s)`);
  };

  // Shared between the inline "Nothing to compare" panel and the popup alert modal, so the
  // explanation copy and the Reset button never drift out of sync between the two surfaces.
  const renderNotComparablePanel = (companyId, result) => (
    <>
      <strong>Nothing to compare.</strong>{" "}
      {(result.reasons || []).includes("STRIPE_SUBSCRIPTION_NOT_FOUND") ? (
        <>
          This company&apos;s Stripe subscription (<code>{result.stripeSubscriptionId}</code>) was not
          found in the currently configured Stripe account. This usually means the
          environment&apos;s Stripe account/mode was switched (e.g. staging data
          copied from production, or the Stripe test account was changed) after this
          subscription ID was stored - the local record and Stripe are now out of
          sync and cannot be compared until this is reconciled.
          <div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              style={{ marginTop: 8 }}
              onClick={() => resetSubscription(companyId)}
              disabled={resetting === companyId}
            >
              {resetting === companyId
                ? "Resetting..."
                : "Reset subscription (disable & re-enable Auto Pay)"}
            </button>
          </div>
        </>
      ) : (
        <>
          This company has no live Stripe subscription
          {(result.reasons || []).length ? ` (${result.reasons.join(", ")})` : ""}
          , so it could not be checked against Stripe.
        </>
      )}
    </>
  );

  return (
    <Fragment>
      <Loader />
      <div id="page-container" className="fade page-sidebar-fixed page-header-fixed">
        <Menu />
        <div id="content" className="content">
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/company">Company</Link>
            </li>
            <li className="breadcrumb-item active">Billing Drift</li>
          </ol>

          <h1 className="page-header">Billing Drift</h1>

          <p className="text-muted" style={{ marginTop: -10, marginBottom: 18 }}>
            Companies whose Stripe subscription charges something other than what their active users
            and add-ons say it should. Under-billing is recoverable; over-billing is reported only.
          </p>

          {summary && (
            <DriftSummaryCards
              items={[
                { label: "Under-billed / month", value: usd(summary.totalMonthlyUnderBilledUsd), tone: "alert-danger" },
                { label: "Over-billed / month", value: usd(summary.totalMonthlyOverBilledUsd), tone: "alert-warning" },
                { label: "Net / month", value: usd(summary.totalMonthlyNetUsd), tone: "alert-success" },
                {
                  label: "Companies with drift",
                  value: `${summary.companiesWithDrift} / ${summary.companiesChecked}`,
                  tone: "alert-info",
                },
              ]}
            />
          )}

          {summary?.staleBreakdownCount > 0 && (
            <div className="alert alert-warning">
              <strong>{summary.staleBreakdownCount}</strong> company(ies) have a stale local copy of their
              subscription. Those rows may look in sync when they are not - use <em>Recheck live</em>. A large
              number here usually means the <code>customer.subscription.updated</code> webhook is not reaching
              this environment.
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <select
                  className="form-select"
                  style={{ width: 190 }}
                  value={driftType}
                  onChange={(e) => { setDriftType(e.target.value); setCurrentPage(1); }}
                >
                  {DRIFT_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>

                <select
                  className="form-select"
                  style={{ width: 210 }}
                  value={cohort}
                  onChange={(e) => { setCohort(e.target.value); setCurrentPage(1); }}
                >
                  {COHORTS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>

                <input
                  type="text"
                  className="form-control"
                  style={{ width: 240 }}
                  placeholder="Search company or email"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />

                <input
                  type="number"
                  className="form-control"
                  style={{ width: 160 }}
                  placeholder="Min $ / month"
                  value={minDeltaUsd}
                  min={0}
                  onChange={(e) => { setMinDeltaUsd(Number(e.target.value) || 0); setCurrentPage(1); }}
                />

                <select
                  className="form-select"
                  style={{ width: 120 }}
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {[20, 50, 100].map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>

                <button type="button" className="btn btn-outline-secondary" onClick={fetchReport}>
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={exportCsv}
                  disabled={!rows.length}
                >
                  Export CSV
                </button>
              </div>

              {loader ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                  <ThreeDots height="60" width="60" color="#348fe2" ariaLabel="loading" />
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Status</th>
                        <th className="text-center">Active users</th>
                        <th className="text-end">Under-billed / mo</th>
                        <th className="text-end">Over-billed / mo</th>
                        <th>Reasons</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!rows.length && (
                        <tr>
                          <td colSpan={7} className="text-center text-muted" style={{ padding: 28 }}>
                            No companies match these filters.
                          </td>
                        </tr>
                      )}

                      {rows.map((row) => (
                        <Fragment key={row.companyId}>
                          <tr>
                            <td>
                              <div style={{ fontWeight: 600 }}>
                                {row.companyName}
                                {row.legacyCompany && (
                                  <span className="badge bg-info" style={{ marginLeft: 6 }}>Legacy</span>
                                )}
                                {row.trialModeEnabled && (
                                  <span className="badge bg-secondary" style={{ marginLeft: 6 }}>Trial</span>
                                )}
                                {row.stale && (
                                  <span className="badge bg-warning text-dark" style={{ marginLeft: 6 }}>Stale</span>
                                )}
                                {row.lastReconciliationMode === "next-cycle" &&
                                  row.lastReconciliationEffectiveDate &&
                                  new Date(row.lastReconciliationEffectiveDate) > new Date() && (
                                    <span
                                      className="badge bg-primary"
                                      style={{ marginLeft: 6 }}
                                      title={`Corrected - takes effect on the invoice dated ${formatDate(row.lastReconciliationEffectiveDate)}, not charged yet`}
                                    >
                                      Pending next invoice
                                    </span>
                                  )}
                              </div>
                              <small className="text-muted">{row.companyEmail}</small>
                              <br />
                              <small className="text-muted">Next billing date: {formatDate(row.nextBillingDate)}</small>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">{row.subscriptionStatus}</span>
                            </td>
                            <td className="text-center">{row.activeUserCount}</td>
                            <td className="text-end" style={{ color: row.monthlyUnderBilledUsd > 0 ? "#b94a48" : "inherit", fontWeight: row.monthlyUnderBilledUsd > 0 ? 600 : 400 }}>
                              {usd(row.monthlyUnderBilledUsd)}
                            </td>
                            <td className="text-end" style={{ color: row.monthlyOverBilledUsd > 0 ? "#8a6d3b" : "inherit" }}>
                              {usd(row.monthlyOverBilledUsd)}
                            </td>
                            <td>
                              {(row.reasons || []).map((reason) => (
                                <span
                                  key={reason}
                                  className="badge bg-light text-dark"
                                  title={REASON_HELP[reason] || reason}
                                  style={{ marginRight: 4, marginBottom: 2, border: "1px solid #ddd" }}
                                >
                                  {reason}
                                </span>
                              ))}
                            </td>
                            <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setExpanded(expanded === row.companyId ? null : row.companyId)}
                                disabled={!row.products?.length && !liveResult[row.companyId]}
                              >
                                {expanded === row.companyId ? "Hide" : "Details"}
                              </button>{" "}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => recheckLive(row.companyId)}
                                disabled={rechecking === row.companyId}
                              >
                                {rechecking === row.companyId ? "Checking..." : "Recheck live"}
                              </button>{" "}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  setSyncProrationMode("next-cycle");
                                  setSyncModalCompanyId(row.companyId);
                                }}
                                disabled={syncing === row.companyId || !row.remediable}
                                title={!row.remediable ? "Not remediable in its current state" : "Review and sync this company's billing"}
                              >
                                {syncing === row.companyId ? "Syncing..." : "Sync now"}
                              </button>
                            </td>
                          </tr>

                          {expanded === row.companyId && (
                            <tr>
                              <td colSpan={7} style={{ background: "#fafbfc" }}>
                                <div style={{ padding: "6px 4px" }}>
                                  <div className="text-muted" style={{ marginBottom: 8 }}>
                                    Subscription <code>{row.stripeSubscriptionId}</code>
                                    {liveResult[row.companyId]?.checkedAt && (
                                      <span style={{ marginLeft: 10 }}>
                                        · {liveResult[row.companyId].comparable ? "checked live at" : "checked at"}{" "}
                                        {new Date(liveResult[row.companyId].checkedAt).toLocaleString()}
                                      </span>
                                    )}
                                  </div>

                                  <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
                                    <thead>
                                      <tr>
                                        <th>Line</th>
                                        <th className="text-center">Expected</th>
                                        <th className="text-center">Charged</th>
                                        <th className="text-center">Difference</th>
                                        <th className="text-end">Unit</th>
                                        <th className="text-end">Per month</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(row.products || []).map((product) => (
                                        <tr key={product.planKey}>
                                          <td>{product.label}</td>
                                          <td className="text-center">{product.expectedQty}</td>
                                          <td className="text-center">{product.actualQty}</td>
                                          <td
                                            className="text-center"
                                            style={{ color: product.direction === "UNDER" ? "#b94a48" : "#8a6d3b", fontWeight: 600 }}
                                          >
                                            {product.deltaQty > 0 ? `+${product.deltaQty}` : product.deltaQty}
                                          </td>
                                          <td className="text-end">{usd(product.unitAmountUsd)}</td>
                                          <td className="text-end">{usd(product.monthlyDeltaUsd)}</td>
                                        </tr>
                                      ))}
                                      {!row.products?.length && (
                                        <tr>
                                          <td colSpan={6} className="text-muted text-center">
                                            No per-line drift recorded.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>

                                  {/* Not comparable: shown once, in the popup alert Modal only
                                      (setDriftAlertCompanyId, below) - this used to also render
                                      inline here via renderNotComparablePanel, duplicating the
                                      same "Invalid Date"/$0.00-safe panel content twice for the
                                      same company. (BR-31) */}

                                  {liveResult[row.companyId]?.comparable && (
                                    <div className="alert alert-info" style={{ marginTop: 12, marginBottom: 0 }}>
                                      <strong>Live from Stripe:</strong>{" "}
                                      under-billed {usd(liveResult[row.companyId].monthlyUnderBilledUsd)}/mo,
                                      over-billed {usd(liveResult[row.companyId].monthlyOverBilledUsd)}/mo,
                                      price drift {usd(liveResult[row.companyId].monthlyPriceDriftUsd)}/mo.{" "}
                                      {liveResult[row.companyId].remediable
                                        ? "This company can be corrected by the remediation script."
                                        : "Not remediable in its current subscription state."}
                                      {(liveResult[row.companyId].warnings || []).map((warning, warningIndex) => (
                                        <div key={`${warning.code}-${warning.planKey || warningIndex}`} style={{ marginTop: 6 }}>
                                          <strong>{warning.code}:</strong> {warning.message}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>

                  <div className="gt-pagination" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <small className="text-muted">
                      Page {pagination.currentPage} of {pagination.totalPages || 1} · {pagination.totalCount} company(ies)
                    </small>
                    <ul className="pagination" style={{ marginBottom: 0 }}>
                      <li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}>
                        <button type="button" className="page-link" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                          Previous
                        </button>
                      </li>
                      <li className={`page-item ${currentPage >= (pagination.totalPages || 1) ? "disabled" : ""}`}>
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      <Modal show={Boolean(driftAlertCompanyId)} onHide={() => setDriftAlertCompanyId(null)} centered>
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title>
            <i className="fa fa-exclamation-triangle me-2 mr-2"></i>
            Billing drift check needs attention
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {driftAlertCompanyId && liveResult[driftAlertCompanyId] && (
            <>
              <p>
                <strong>{liveResult[driftAlertCompanyId].companyName}</strong>
              </p>
              {renderNotComparablePanel(driftAlertCompanyId, liveResult[driftAlertCompanyId])}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setDriftAlertCompanyId(null)}>
            Dismiss
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(syncModalCompanyId)} onHide={() => !syncing && setSyncModalCompanyId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Sync billing for company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This re-checks the company against Stripe at click time and, if it is still out of
            sync, raises the affected item quantities and prices to match. Over-billed amounts are
            never touched or credited.
          </p>
          {rows.find((r) => r.companyId === syncModalCompanyId)?.nextBillingDate && (
            <p className="text-muted" style={{ marginBottom: 12 }}>
              <strong>Next billing date:</strong>{" "}
              {formatDate(rows.find((r) => r.companyId === syncModalCompanyId).nextBillingDate)}
            </p>
          )}
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="prorationNextCycle"
              checked={syncProrationMode === "next-cycle"}
              onChange={() => setSyncProrationMode("next-cycle")}
            />
            <label className="form-check-label" htmlFor="prorationNextCycle">
              <strong>Apply at next billing cycle</strong> — no charge now; the correction takes
              effect on the next invoice.
            </label>
          </div>
          <div className="form-check" style={{ marginTop: 8 }}>
            <input
              className="form-check-input"
              type="radio"
              id="prorationChargeNow"
              checked={syncProrationMode === "charge-now"}
              onChange={() => setSyncProrationMode("charge-now")}
            />
            <label className="form-check-label" htmlFor="prorationChargeNow">
              <strong>Charge prorated amount now</strong> — invoices the company immediately for
              the prorated difference in the current billing period. If the subscription is
              currently trialing, Stripe generates no prorations, so this silently falls back to
              the next-cycle behavior instead.
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setSyncModalCompanyId(null)}
            disabled={syncing === syncModalCompanyId}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => syncCompany(syncModalCompanyId, syncProrationMode)}
            disabled={syncing === syncModalCompanyId}
          >
            {syncing === syncModalCompanyId ? "Syncing..." : "Confirm Sync"}
          </button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default BillingDrift;
